import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { ElectronicModel } from '@/types';
import { useUpdateElectronicModelMutation } from '@/services';
import { messages } from '@/utils/helpers';
import { SearchElectronicBrands } from '@/pages/ElectronicBrands/components';

export interface UpdateElectronicModelProps extends DialogProps {
  electronicModel: ElectronicModel;
  onClose(): void;
}

const UpdateElectronicModel: React.FC<UpdateElectronicModelProps> = ({ electronicModel, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateElectronicModel, updateElectronicModelResponse] = useUpdateElectronicModelMutation();

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange } = useFormik<ElectronicModel>({
    initialValues: electronicModel ?? {
      name: '',
      electronic_brand: null,
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
      electronic_brand: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
    }),
    async onSubmit(electronicModel, { resetForm }) {
      try {
        await updateElectronicModel(electronicModel).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать модель электроники</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={updateElectronicModelResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <SearchElectronicBrands
                value={values.electronic_brand}
                onChange={(event, option) => setFieldValue('electronic_brand', option)}
                disabled={updateElectronicModelResponse.isLoading}
                getOptionLabel={(option) => option.name}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    name="electronic_brand"
                    label="Модель электроники"
                    placeholder="Выберите из списка"
                    error={Boolean(touched.electronic_brand && errors.electronic_brand)}
                    helperText={touched.electronic_brand && errors.electronic_brand}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={updateElectronicModelResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={updateElectronicModelResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateElectronicModel;
